import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';

import {add, deleteRecord, fetch, fetchRecords, update,} from 'services/ErrorMessagesService';

import {normalize, schema} from 'normalizr';

const entityKey = 'errorMessages';

export const entitySchema = new schema.Entity(entityKey);
export const arrayOfRecords = new schema.Array(entitySchema);

const initialState = {
    byId: {},
    ids: [],
    totalCount: 0,
    loading: false,
    error: '',
};

// genenrates pending, fulfilled and rejected
export const fetchErrorMessages = createAsyncThunk(
    `${entityKey}/fetchRecords`,
    (query) => {
        return fetchRecords(query).then((response) => response.Items);
    }
);

export const fetchErrorMessage = createAsyncThunk(
    `${entityKey}/fetch`,
    (id) => {
        return fetch(id).then((response) => response);
    }
);

export const saveErrorMessage = createAsyncThunk(
    `${entityKey}/add`,
    (payload) => {
        return add(payload).then((response) => response);
    }
);

export const updateErrorMessage = createAsyncThunk(
    `${entityKey}/update`,
    ({ id, record }) => {
        return update(id, record).then((response) => response);
    }
);

export const deleteErrorMessage = createAsyncThunk(
    `${entityKey}/deleteRecord`,
    (id) => {
        return deleteRecord(id).then((response) => response);
    }
);

const errorMessagesSlice = createSlice({
    name: entityKey,
    initialState,
    extraReducers: (builder) => {
        // FETCH MANY
        builder.addCase(fetchErrorMessages.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(fetchErrorMessages.fulfilled, (state, action) => {
            state.loading = false;
            const normalized = normalize(action.payload, arrayOfRecords);
            const { entities, result: ids } = normalized;
            state.byId = { ...entities[entityKey] };
            state.ids = [...new Set(ids)];
        });

        builder.addCase(fetchErrorMessages.rejected, (state, action) => {
            state.byId = {};
            state.ids = [];
            state.loading = false;
            state.error = action.error.message;
        });

        // FETCH ONE
        builder.addCase(fetchErrorMessage.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(fetchErrorMessage.fulfilled, (state, action) => {
            state.byId[action.payload.id] = action.payload;
            state.loading = false;
        });

        builder.addCase(fetchErrorMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // SAVE ONE
        builder.addCase(saveErrorMessage.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(saveErrorMessage.fulfilled, (state, action) => {
            const { payload: record } = action;

            if (typeof record.uuid !== 'undefined') {
                state.byId[record.uuid] = record;
                state.ids.push(record.uuid);
            } else {
                state.byId[record.id] = record;
                state.ids.push(record.id);
            }
            state.totalCount = state.totalCount + 1;

            state.loading = false;
        });

        builder.addCase(saveErrorMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // UPDATE ONE
        builder.addCase(updateErrorMessage.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(updateErrorMessage.fulfilled, (state, action) => {
            const { payload: record } = action;

            if (typeof record.uuid !== 'undefined') {
                state.byId[record.uuid] = record;
            } else {
                state.byId[record.id] = record;
            }
            state.loading = false;
        });

        builder.addCase(updateErrorMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });

        // DELETE ONE
        builder.addCase(deleteErrorMessage.pending, (state) => {
            state.loading = true;
        });

        builder.addCase(deleteErrorMessage.fulfilled, (state, action) => {
            const { payload: recordId } = action;

            delete state.byId[recordId];
            state.ids = state.ids.filter(
                (item) => item.toString() !== recordId.toString()
            );
            state.totalCount = state.totalCount - 1;

            state.loading = false;
        });

        builder.addCase(deleteErrorMessage.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });
    },
});

export default errorMessagesSlice.reducer;
